import React, { createContext, useEffect, useState, useContext } from "react";
import {
  refreshAccessToken,
  setAccessToken,
  registerTokenObserver,
} from "@/api/tokenManager";
import { CircularProgress, Box } from "@mui/material";

interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    // 1. Subscribe to token changes
    registerTokenObserver((token) => {
      setIsAuthenticated(!!token);
    });

    // 2. Initial auth check via cookie
    const initAuth = async () => {
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          setAccessToken(newToken);
        }
      } catch (error) {
        console.error("Auth initialization failed", error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={{ isLoading, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};
